import { useEffect, useMemo, useRef, useState } from 'react';
import { useZoneStore } from '../../store/zoneStore';
import { useDashboardStore, matchesZoneFilter } from '../../store/dashboardStore';
import { switchZone } from '../../services/zoneSwitchService';
import { SearchIcon } from '../header/searchStyles';

const MAX_SUGGESTIONS = 8;

/** True while the user types into another field — the "/" shortcut must not steal that key. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

interface ZoneSearchProps {
  className?: string;
}

/**
 * Zone search of the cross-zone pages (dashboard, Netzmonitoring), shown in the page toolbar:
 * typing narrows the zone cards resp. every figure of the Netzmonitoring page live
 * (`dashboardStore.zoneFilter`, shared by both pages), the dropdown offers the matching zones,
 * Enter opens the first match on the map. "/" focuses the field from anywhere on the page.
 */
export function ZoneSearch({ className = 'w-80' }: ZoneSearchProps) {
  const zones = useZoneStore(s => s.zones);
  const zoneFilter = useDashboardStore(s => s.zoneFilter);
  const setZoneFilter = useDashboardStore(s => s.setZoneFilter);
  const [open, setOpen] = useState(false);
  const [busyZoneId, setBusyZoneId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const matches = useMemo(() => {
    if (!zoneFilter.trim()) return [];
    return zones
      .filter(z => matchesZoneFilter(z, zoneFilter))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [zones, zoneFilter]);

  const openZone = async (zoneId: string) => {
    setOpen(false);
    setBusyZoneId(zoneId);
    try {
      await switchZone(zoneId, { tab: 'map', waitForMapResize: true });
    } finally {
      setBusyZoneId(null);
    }
  };

  const showDropdown = open && zoneFilter.trim().length > 0;

  return (
    <div className={`relative ${className}`}>
      <div className="relative flex items-center">
        <SearchIcon className="absolute left-2.5 w-4 h-4 text-gray-400 dark:text-primary-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="search"
          placeholder="Zone suchen (Name oder ID) …"
          value={zoneFilter}
          onChange={(e) => { setZoneFilter(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && matches.length > 0) void openZone(matches[0].id);
            if (e.key === 'Escape') {
              if (open) setOpen(false);
              else setZoneFilter('');
            }
          }}
          className="w-full pl-8 pr-12 py-1.5 bg-white dark:bg-primary-700 border border-gray-300 dark:border-primary-500 text-gray-900 dark:text-primary-100 placeholder:text-gray-500 dark:placeholder:text-primary-400 rounded-md shadow-sm focus:outline-hidden focus:ring-1 focus:ring-accent-primary text-sm [&::-webkit-search-cancel-button]:hidden"
          aria-label="Zonen filtern"
          title="Zonen nach Name oder ID filtern — Enter öffnet den ersten Treffer auf der Karte, Taste / springt hierher"
        />
        {zoneFilter ? (
          <button
            onClick={() => { setZoneFilter(''); setOpen(false); }}
            className="absolute right-2 p-0.5 rounded text-gray-400 hover:text-gray-700 dark:text-primary-400 dark:hover:text-primary-100"
            title="Suche zurücksetzen"
            aria-label="Suche zurücksetzen"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : (
          <kbd className="absolute right-2 px-1.5 rounded border border-gray-300 dark:border-primary-500 text-[10px] text-gray-400 dark:text-primary-400 pointer-events-none" aria-hidden="true">/</kbd>
        )}
      </div>

      {showDropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 w-full min-w-[18rem] max-h-72 overflow-y-auto bg-white dark:bg-primary-800 border border-gray-300 dark:border-primary-500 rounded-lg shadow-xl z-50">
            {matches.length === 0 && (
              <div className="p-3 text-xs text-gray-500 dark:text-primary-300">
                Keine Zone passt zu „{zoneFilter.trim()}"
              </div>
            )}
            {matches.slice(0, MAX_SUGGESTIONS).map(zone => (
              <button
                key={zone.id}
                onClick={() => void openZone(zone.id)}
                disabled={busyZoneId !== null}
                className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-primary-600 transition-colors duration-150 border-b border-gray-200 dark:border-primary-600 last:border-b-0 disabled:opacity-60"
              >
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: zone.color }} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-sm text-gray-900 dark:text-primary-100 truncate">{zone.name}</span>
                  {zone.name !== zone.id && (
                    <span className="block text-[11px] text-gray-500 dark:text-primary-400 truncate">{zone.id}</span>
                  )}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-primary-500 shrink-0">
                  {busyZoneId === zone.id ? 'lädt…' : 'Karte'}
                </span>
              </button>
            ))}
            {matches.length > MAX_SUGGESTIONS && (
              <div className="px-3 py-2 text-[11px] text-gray-500 dark:text-primary-400">
                {matches.length - MAX_SUGGESTIONS} weitere — die Seite ist auf alle Treffer eingegrenzt
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
